/**
 * v3.99ag — Market Intelligence status-board.
 *
 * Bloomberg-terminal styled grid of compact data cards. Sits below the
 * dual-pulse ActivityTickers on Home as the static counterpart — readable
 * status at a glance, no motion. Each card is clickable when an `href` is
 * available. Slots without eligible data are silently omitted.
 *
 * Design contract:
 *   - dense, compact, mono-spaced labels
 *   - subtle category accent on the eyebrow label (green / gold / blue / red)
 *   - hover lifts the border to gold; no glow, no flash, no emoji
 *   - mobile: 1 column · sm: 2 · md: 3 · lg: 4
 *   - whole panel hides itself when the API returns zero slots
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, Gavel, Sparkles, TrendingUp, ArchiveRestore,
  BadgeCheck, Sprout, Gift, Crown, ArrowUpRight, ShieldCheck,
} from "lucide-react";
import { api } from "../lib/apiClient";
import { usePlatform } from "../context/PlatformContext";
import { useAuth } from "../context/AuthContext";

// ---------------------------------------------------------------------------
// Slot metadata — controls eyebrow label, icon, and accent color per slot.
// Display order is fixed for layout stability.
// ---------------------------------------------------------------------------
const SLOTS = [
  ["auction_ending_soon",       { label: "Auction · ending soon",   Icon: Gavel,           accent: "var(--gold)" }],
  ["featured_drop",             { label: "Featured drop",           Icon: Sparkles,        accent: "var(--gold)" }],
  ["giveaway_closing_soon",     { label: "Giveaway · closing soon", Icon: Gift,            accent: "var(--gold)" }],
  ["trending_cultivar",         { label: "Trending cultivar",       Icon: TrendingUp,      accent: "#7CC9FF" }],
  ["new_verified_grow",         { label: "New verified grow",       Icon: Sprout,          accent: "var(--primary-text)" }],
  ["recently_breeder_verified", { label: "Recently breeder verified", Icon: BadgeCheck,    accent: "var(--primary-text)" }],
  ["latest_vault_preserved",    { label: "Latest Vault preservation", Icon: ArchiveRestore, accent: "#D8B36C" }],
  ["rising_breeder",            { label: "Rising breeder",          Icon: Crown,           accent: "var(--gold)" }],
];

export default function MarketIntelligence() {
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  // v3.99av — Vault Heat mini surface. Loads independently; failures
  // are silent so MI never blocks on Vault data.
  const [vaultHeat, setVaultHeat] = useState(null);
  // v3.99ax — Vault is hidden from public/collector view during Bank
  // Approval Mode. Admins + vendors still see all Vault telemetry so
  // their internal workflows are never blocked.
  const { bank_approval_mode } = usePlatform();
  const { user } = useAuth();
  const isInternal = user && (user.role === "admin" || user.role === "vendor");
  const applyBam = bank_approval_mode && !isInternal;

  useEffect(() => {
    let alive = true;
    api.get("/public/market-intelligence")
      .then((r) => { if (alive) setSlots(r.data?.slots || {}); })
      .catch(() => { if (alive) setSlots({}); })
      .finally(() => { if (alive) setLoading(false); });
    // v3.99ax — skip Vault Heat fetch entirely under BAM for non-internal
    // viewers. Saves a request and prevents the strip from briefly
    // flashing before the conditional render hides it.
    if (!applyBam) {
      api.get("/vault/heat")
        .then((r) => { if (alive) setVaultHeat(r.data || null); })
        .catch(() => { /* silent */ });
    }
    return () => { alive = false; };
  }, [applyBam]);

  // v3.99ah — Rising Breeder fallback. When the backend has no meaningful
  // rising-breeder data, surface a quiet "Applications open" recruitment
  // card in that exact slot. Restrained, premium, NEVER ad-like. Once
  // real breeder activity exists, the backend fills the slot and this
  // fallback disappears naturally.
  const slotsWithFallbacks = { ...slots };
  if (!slotsWithFallbacks.rising_breeder) {
    slotsWithFallbacks.rising_breeder = {
      title: "Apply as a breeder",
      subtitle: "Founding cohort · invitation curated",
      meta: "Applications open",
      href: "/join/breeder",
      _fallback: true,
    };
  }
  // v3.99ax — Vault hidden during BAM for public/collector visitors. Drop
  // any Vault-flavored slot from the status board so the public surface
  // never references the preservation institution while in processor review.
  if (applyBam) {
    delete slotsWithFallbacks.latest_vault_preserved;
  }

  const visible = SLOTS.filter(([key]) => slotsWithFallbacks[key]);
  if (!loading && visible.length === 0) return null;

  return (
    <section
      className="border-t border-b border-[var(--border)]"
      style={{ background: "var(--bg)" }}
      data-testid="market-intelligence-panel"
      aria-label="Market intelligence"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <div className="label-eyebrow inline-flex items-center gap-2"
                 style={{ color: "var(--secondary)" }}>
              <Activity className="w-3 h-3" /> Market intelligence
            </div>
            <h2 className="font-serif text-2xl md:text-3xl tracking-tighter mt-1"
                style={{ color: "var(--text)" }}>
              Forge Intelligence
            </h2>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] hidden sm:inline"
                style={{ color: "var(--text-muted)" }}>
            Refreshes on load
          </span>
        </div>

        {/* v3.99aw — Mobile: horizontal scroll-snap carousel (one card +
            peek of next) to dramatically reduce vertical stacking on
            small screens while preserving the full status board. Desktop
            keeps the dense 4-column grid that reads as a command center.
            Scroll-padding + snap-mandatory give the carousel a clean
            stop on every card. */}
        <div className="md:hidden -mx-4 px-4 overflow-x-auto snap-x snap-mandatory flex gap-3 pb-2 forge-carousel"
             data-testid="market-intelligence-carousel"
             style={{ scrollPaddingLeft: "1rem" }}>
          {visible.map(([key, meta]) => (
            <div key={key} className="snap-start shrink-0 w-[78%]">
              <Card slotKey={key} meta={meta} data={slotsWithFallbacks[key]} />
            </div>
          ))}
          {/* trailing spacer so the last card can snap-align with peek */}
          <div className="shrink-0 w-2" aria-hidden />
        </div>
        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
             data-testid="market-intelligence-grid">
          {visible.map(([key, meta]) => (
            <Card key={key} slotKey={key} meta={meta} data={slotsWithFallbacks[key]} />
          ))}
        </div>

        {/* v3.99av — Vault Heat mini surface. Quiet institutional
            telemetry inside Market Intelligence. Renders nothing until
            at least one active preservation exists.
            v3.99aw — On mobile, this sits below the swipe carousel and
            uses tighter vertical metric (mt-4 vs mt-5) so the whole
            Forge Intelligence section reads as one calm block.
            v3.99ax — Hidden entirely during Bank Approval Mode for
            public/collector visitors so Vault references never surface
            during processor underwriting. Admins/vendors keep seeing it. */}
        {!applyBam && vaultHeat && (vaultHeat.active_total || 0) > 0 && (
          <Link to="/vault"
                className="mt-4 md:mt-5 block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors p-3"
                style={{ borderRadius: 2 }}
                data-testid="mi-vault-heat">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.18em] text-[10px]"
                    style={{ color: "var(--gold)" }}>
                <ShieldCheck className="w-3 h-3" />
                Vault telemetry
              </span>
              <span className="text-[12px]" style={{ color: "var(--text-2)" }}>
                <span className="font-mono" style={{ color: "var(--text)" }}>
                  {vaultHeat.active_total}
                </span>
                <span className="ml-1.5 font-mono uppercase tracking-[0.14em] text-[10px]"
                      style={{ color: "var(--text-muted)" }}>
                  active
                </span>
              </span>
              {vaultHeat.new_this_quarter > 0 && (
                <span className="text-[12px]" style={{ color: "var(--text-2)" }}>
                  <span className="font-mono" style={{ color: "var(--gold)" }}>
                    +{vaultHeat.new_this_quarter}
                  </span>
                  <span className="ml-1.5 font-mono uppercase tracking-[0.14em] text-[10px]"
                        style={{ color: "var(--text-muted)" }}>
                    this quarter
                  </span>
                </span>
              )}
              {vaultHeat.contributor_count > 0 && (
                <span className="text-[12px]" style={{ color: "var(--text-2)" }}>
                  <span className="font-mono" style={{ color: "var(--text)" }}>
                    {vaultHeat.contributor_count}
                  </span>
                  <span className="ml-1.5 font-mono uppercase tracking-[0.14em] text-[10px]"
                        style={{ color: "var(--text-muted)" }}>
                    contributors
                  </span>
                </span>
              )}
              <span className="ml-auto inline-flex items-center gap-1 font-mono uppercase tracking-[0.18em] text-[10px]"
                    style={{ color: "var(--text-muted)" }}>
                Vault registry
                <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </Link>
        )}
      </div>
      <style>{`
        .forge-carousel { scrollbar-width: none; -ms-overflow-style: none; }
        .forge-carousel::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}

function Card({ slotKey, meta, data }) {
  const { Icon, label, accent } = meta;
  const fallback = !!data._fallback;
  // v3.99ah — fallback (recruitment) cards get a subtler styling so they
  // never feel like an ad. The icon + label use muted text; the heading
  // remains premium; "Applications open" sits as a quiet badge instead
  // of the standard meta footer.
  const useAccent = fallback ? "var(--text-muted)" : accent;
  // v3.99d.289 — small thumbnail when backend provides image_url.
  const thumb = !fallback && data.image_url;
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.18em] text-[10px]"
              style={{ color: useAccent }}>
          <Icon className="w-3 h-3" />
          {fallback ? "Founding cohort" : label}
        </span>
        {data.href && (
          <ArrowUpRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity"
                        style={{ color: "var(--text-muted)" }} />
        )}
      </div>
      <div className="flex gap-3 items-start">
        {thumb && (
          <img src={data.image_url} alt="" loading="lazy"
               width="56" height="56"
               className="w-14 h-14 object-cover shrink-0 bg-[var(--surface-2)] border border-[var(--border)]"
               style={{ borderRadius: 2 }}
               data-testid={`mi-card-${slotKey}-thumb`} />
        )}
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg leading-tight tracking-tight line-clamp-2 mb-1"
               style={{ color: "var(--text)" }}>
            {data.title}
          </div>
          {data.subtitle && (
            <div className="text-xs line-clamp-1" style={{ color: "var(--text-2)" }}>
              {data.subtitle}
            </div>
          )}
        </div>
      </div>
      {data.meta && (
        <div className="font-mono text-[10px] uppercase tracking-[0.16em] mt-2 pt-2 border-t border-[var(--border)] inline-flex items-center gap-1.5"
             style={{ color: fallback ? "var(--gold)" : "var(--text-muted)" }}>
          {fallback && <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gold)" }} />}
          {data.meta}
        </div>
      )}
    </>
  );

  const className = "group block bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold-soft)] transition-colors p-4 h-full";

  return data.href ? (
    <Link to={data.href} className={className}
          style={{ borderRadius: 2 }}
          data-testid={`mi-card-${slotKey}${fallback ? "-fallback" : ""}`}>
      {inner}
    </Link>
  ) : (
    <div className={className} style={{ borderRadius: 2 }}
         data-testid={`mi-card-${slotKey}${fallback ? "-fallback" : ""}`}>
      {inner}
    </div>
  );
}
